import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import SfxTable from "src/components/table/SfxTable.vue"
import { getDB } from "src/db/databases"
import { useFilterStore } from "src/stores/filterStore"
import { useSearchStore } from "src/stores/searchStore"
import { useSupportStore } from "src/stores/supportStore"
import { Log } from "src/util/classes/log"
import { MissingEntityType } from "src/util/enums/entityType"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import {
  fetchAttributeCharListData,
  fetchAttributeNumericListData,
  fetchAttributeSelectListData,
  fetchAttributeTemporalListData,
  fetchIssueOnItemListData,
  fetchIssuePriorityListData,
  fetchItemClassListData,
  fetchItemListData,
  fetchItemModelListData,
  getItemClassListData,
} from "src/util/functions/entityData"
import {
  filterItemsBasedOnCorrectiveWork,
  filterItemsBasedOnIssues,
  filterItemsBasedOnRegularWork,
} from "src/util/functions/filters"
import {
  getItemTableAttributesColumns,
  sortItemData,
  toItemTableData,
} from "src/util/functions/item"
import { getWorkspaceHomeBreadcrumb } from "src/util/functions/workspace"
import { Breadcrumbs } from "src/util/types/components/sfxBreadcrumb"
import { RowData, StatusColumnNames, TableColumn } from "src/util/types/components/sfxTable"
import { Item, ItemTableData } from "src/util/types/dataModels/item"
import { WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"
import { computed, onBeforeMount, PropType, ref, watch, watchEffect } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

const statusColumnCount = 3


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxWorkOrderItemAssignment',
  props: {
  workOrder: {
    type: Object as PropType<WorkOrderRegularItem>,
    required: true,
  },

  entitiesIncluded: {
    type: Array as PropType<Item[]>,
    required: true,
  },

  entitiesPerformed: {
    type: Array as PropType<Item[]>,
    required: true,
  },

  isDisplayingToDoEntities: {
    type: Boolean,
  },

  isReadOnly: {
    type: Boolean,
  },
},
  setup(__props, { expose: __expose }) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const filterStore = useFilterStore()
const supportStore = useSupportStore()
const searchStore = useSearchStore()

const breadcrumbs = ref<Breadcrumbs>([])

const itemsAvailableList = ref<RowData<ItemTableData>[]>([])
const itemsAvailableListFiltered = ref<RowData<ItemTableData>[]>([])
const itemsTableAvailableData = computed(() => itemsAvailableListFiltered.value)
const itemsCompletedList = ref<RowData<ItemTableData>[]>([])
const itemsTableCompletedData = computed(() => itemsCompletedList.value)
const noData = ref(MissingEntityType.ITEM_NOT_FOUND)
const noMatchingData = ref(MissingEntityType.ITEM_NO_MATCHES)

const mainColumn = (row: RowData<ItemTableData>) => row.itemData.designation
const uniqueColumn = (row: RowData<ItemTableData>) => row.itemData.id
const mainColumnLabel = ref(t("items.table_column_designation_label"))

const selectionEnabled = ref(!props.isReadOnly)
const selected = ref<ItemTableData[]>([])
const initialSelected = ref<ItemTableData[]>([])
const loadingData = ref(true)

const sfxTableAvailable = ref<typeof SfxTable | undefined>()
const sfxTableCompleted = ref<typeof SfxTable | undefined>()
const filteredAvailableDataCount = computed(() => sfxTableAvailable.value?.filteredDataCount || 0)
const filteredCompletedDataCount = computed(() => sfxTableCompleted.value?.filteredDataCount || 0)

const initialSelectIds = computed(() => initialSelected.value.map((i) => i.itemData.id).sort())
const selectIds = computed(() => selected.value.map((i) => i.itemData.id).sort())

const visibleColumns = ref([
  "item_id",
  "item_class",
  "model",
  "location_id",
  "location_access",
  "layout",
])

const itemColumns = computed<Array<TableColumn>>(() => {
  const arr = [
    {
      name: "item_id",
      switchable: true,
      sortable: true,
      searchable: true,
      label: t("items.table_column_item_id_label"),
      field: (row: ItemTableData) => row.itemData.item_id,
    },
    {
      name: "item_class",
      switchable: true,
      sortable: true,
      searchable: false,
      label: t("items.table_column_item_class_label"),
      field: (row: ItemTableData) => row.itemClassData?.designation,
      id: (row: ItemTableData) => row.itemClassData?.id,
    },
    {
      name: "model",
      switchable: true,
      sortable: true,
      emptyText: t("sfxTable.empty_no_model"),
      label: t("items.table_column_model_label"),
      field: (row: ItemTableData) => row.itemModelData?.designation,
      id: (row: ItemTableData) => row.itemModelData?.id,
    },
    {
      name: "location_id",
      switchable: true,
      sortable: true,
      emptyText: t("sfxTable.empty_no_location"),
      label: t("items.table_column_location_id_label"),
      field: "", // TODO: Should display location ID when we have it
    },
    {
      name: "location_access",
      switchable: true,
      sortable: true,
      emptyText: t("sfxTable.empty_no_location"),
      label: t("items.table_column_location_label"),
      field: "", // TODO: Should display location access when we have it
    },
    {
      name: "layout",
      switchable: true,
      sortable: true,
      emptyText: t("sfxTable.empty_no_location"),
      label: t("items.table_column_layout_label"),
      field: "", // TODO: Should display layout designation when we have it
    },
  ]

  return attributeColumns.value ? [...arr, ...attributeColumns.value] : arr
})

const attributeColumns = ref()

const statusColumnsSort: StatusColumnNames[] = ["issue", "work-order", "change-request"]

// 1. Issue
// 2. Work Order
// 3. Change Request
const performedRowClass = ref("sfx-table__td-status-performed")

//endregion

//region Lifecycle

onBeforeMount(async () => {
  searchStore.domain = "items"
  supportStore.setFeedbackData(t("workOrder.title_work_order_items"))

  try {
    await fetchAPIData()
    await assignItemTableData()
  } catch (fetchError) {
    Log.error({
      message: "There was an error trying to fetch and populate data for items",
      error: fetchError,
    })
  } finally {
    loadingData.value = false
  }
})

//endregion

//region Functions

function resetSelection() {
  selected.value = [...initialSelected.value]
}

async function fetchAPIData() {
  await fetchItemClassListData()
  await Promise.all([
    fetchItemListData(),
    fetchIssueOnItemListData(),
    fetchIssuePriorityListData(),
    fetchItemModelListData(),
    fetchAttributeCharListData(),
    fetchAttributeNumericListData(),
    fetchAttributeSelectListData(),
    fetchAttributeTemporalListData(),
  ])
  attributeColumns.value = await getItemTableAttributesColumns()
}

async function assignItemTableData() {
  const skipIds = props.entitiesPerformed
    .map((i) => i.id)
    .concat(props.entitiesIncluded.map((i) => i.id))

  const itemData = await getItemData(props.workOrder?.work_domain, skipIds)

  const selectedItems = await Promise.all(props.entitiesIncluded.map((i) => toItemTableData(i)))
  selected.value = selectedItems
  initialSelected.value = [...selectedItems]

  itemsAvailableList.value = itemData.concat(selectedItems).sort(sortItemData)
  itemsAvailableListFiltered.value = itemsAvailableList.value

  itemsCompletedList.value = await Promise.all(
    props.entitiesPerformed.map(async (i) => {
      const data = await toItemTableData(i)
      data.preSelectState = true
      return data
    })
  )
}

async function getItemData(
  workDomainId: string,
  skipItemIds: string[]
): Promise<RowData<ItemTableData>[]> {
  const db = await getDB()
  const itemClasses = await getItemClassListData()
  const filteredItemClassIds = itemClasses
    .filter(
      (i) =>
        i.restricted_to_work_domains.length === 0 ||
        i.restricted_to_work_domains.includes(workDomainId)
    )
    .map((i) => i.id)

  const itemDocuments = await db.item
    .find({
      selector: {
        $and: [{ item_class: { $in: filteredItemClassIds } }, { id: { $nin: skipItemIds } }],
      },
    })
    .exec()

  return await Promise.all(
    itemDocuments.map<Promise<RowData<ItemTableData>>>((i) => toItemTableData(i))
  )
}

//endregion

//region Setup Code

watchEffect(async () => {
  const count = props.isDisplayingToDoEntities
    ? filteredAvailableDataCount.value
    : filteredCompletedDataCount.value
  const total = props.isDisplayingToDoEntities
    ? itemsTableAvailableData.value.length
    : itemsTableCompletedData.value.length

  breadcrumbs.value = [
    await getWorkspaceHomeBreadcrumb(),
    {
      text: t("workOrder.title"),
      link: { name: NamedManagerPath.SELECT_WORK_ORDER },
      highlight: true,
    },
    {
      text: t("workOrder.title_simple"),
    },
    {
      text: t("workOrder.title_work_order_items"),
    },
    { current: count, total, entity: "item" },
  ]
})

watch(
  () => [filterStore.itemsPageFilters, itemsAvailableList.value],
  () => {
    if (!filterStore.itemsPageFilters.hasFilters) {
      itemsAvailableListFiltered.value = itemsAvailableList.value
    } else {
      itemsAvailableListFiltered.value = itemsAvailableList.value.filter(
        (item) =>
          // Issue filters
          filterItemsBasedOnIssues(item) &&
          // Performed regular work filters
          filterItemsBasedOnRegularWork(item) &&
          // Performed corrective work filters
          filterItemsBasedOnCorrectiveWork(item)
      )
    }
  },
  { deep: true }
)

__expose({
  resetSelection,
  breadcrumbs,
  initialSelectIds,
  selectIds,
})

//endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(SfxTable, {
        ref_key: "sfxTableAvailable",
        ref: sfxTableAvailable,
        columns: itemColumns.value,
        "visible-columns": visibleColumns.value,
        loading: loadingData.value,
        data: itemsTableAvailableData.value,
        "no-data": noData.value,
        "no-matching-data": noMatchingData.value,
        "unique-column": uniqueColumn,
        "main-column": mainColumn,
        "main-column-label": mainColumnLabel.value,
        "selection-enabled": selectionEnabled.value,
        "status-columns-sort": statusColumnsSort,
        "status-columns-count": statusColumnCount,
        selected: selected.value,
        "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((selected).value = $event)),
        "has-filters": _unref(filterStore).itemsPageFilters.hasFilters,
        "entity-for-work-order-page": "",
        entity: "item"
      }, null, 8, ["columns", "visible-columns", "loading", "data", "no-data", "no-matching-data", "main-column-label", "selection-enabled", "selected", "has-filters"])
    ], 512), [
      [_vShow, props.isDisplayingToDoEntities]
    ]),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(SfxTable, {
        ref_key: "sfxTableCompleted",
        ref: sfxTableCompleted,
        columns: itemColumns.value,
        "visible-columns": visibleColumns.value,
        loading: loadingData.value,
        data: itemsTableCompletedData.value,
        "no-data": noData.value,
        "no-matching-data": noMatchingData.value,
        "unique-column": uniqueColumn,
        "main-column": mainColumn,
        "main-column-label": mainColumnLabel.value,
        "status-columns-sort": statusColumnsSort,
        "status-columns-count": statusColumnCount,
        entity: "item",
        "selection-enabled": selectionEnabled.value,
        "enable-pre-selected-mode": "",
        "row-class": performedRowClass.value,
        "icon-filter": false
      }, null, 8, ["columns", "visible-columns", "loading", "data", "no-data", "no-matching-data", "main-column-label", "selection-enabled", "row-class"])
    ], 512), [
      [_vShow, !props.isDisplayingToDoEntities]
    ])
  ], 64))
}
}

})